import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Eye, EyeOff, Lock, CheckCircle, AlertCircle, Loader2, ArrowLeft, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function SetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [setupComplete, setSetupComplete] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setVerifying(false);
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/verify-setup-token/${token}`);
      setTokenValid(true);
      setEmail(response.data.email);
      setName(response.data.name || '');
    } catch (error) {
      setTokenValid(false);
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/set-password`, {
        token,
        password: formData.password
      });
      
      // Store session and user data
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
      }
      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      
      setSetupComplete(true);
      toast.success('Account setup complete! Welcome to theailedger.ai!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set password');
    } finally {
      setLoading(false);
    }
  };

  const goToDashboard = () => {
    window.location.href = '/dashboard';
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#00F0FF] mx-auto mb-4" />
          <p className="text-neutral-400">Verifying your invitation...</p>
        </div>
      </div>
    );
  }

  // No token or invalid token
  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold mb-4">Invalid or Expired Link</h1>
          <p className="text-neutral-400 mb-8">
            This invitation link is invalid or has expired. Please contact support if you believe this is an error.
          </p>
          <Button
            onClick={() => navigate('/')}
            className="bg-[#00F0FF] hover:bg-[#FFB800] text-black rounded-full px-8"
            data-testid="back-to-home-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  // Setup complete - show success and redirect to dashboard
  if (setupComplete) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#00F0FF] to-[#00FF94] flex items-center justify-center mx-auto mb-6">
            <Crown className="w-10 h-10 text-black" />
          </div>
          <h1 className="text-2xl font-bold mb-4">Welcome, VIP Creator!</h1>
          <p className="text-neutral-400 mb-8">
            Your account has been set up successfully. You're now ready to start uploading your AI products and earning!
          </p>
          <Button
            onClick={goToDashboard}
            className="bg-gradient-to-r from-[#00F0FF] to-[#00FF94] hover:opacity-90 text-black rounded-full px-8 font-bold"
            data-testid="go-to-dashboard-btn"
          >
            Go to Creator Dashboard →
          </Button>
        </div>
      </div>
    );
  }

  // Password setup form
  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        <div className="bg-neutral-900/50 border border-[#00F0FF]/20 rounded-2xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#00F0FF] to-[#00FF94] flex items-center justify-center mx-auto mb-4">
              <Crown className="w-8 h-8 text-black" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Welcome, VIP Creator!</h1>
            <p className="text-neutral-400 text-sm">
              {name && <span className="text-[#00F0FF]">{name}</span>}
              {name && <br />}
              Set up your password to access your creator dashboard
            </p>
            <p className="text-neutral-500 text-xs mt-2">
              Account: <span className="text-[#00F0FF]">{email}</span>
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="set-password-form">
            <div>
              <label className="block text-sm font-semibold mb-2">Create Password</label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  className="bg-neutral-800 border-white/10 pr-10"
                  required
                  minLength={8}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-white transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-neutral-500 mt-1">Must be at least 8 characters</p>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2">Confirm Password</label>
              <div className="relative">
                <Input
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  placeholder="••••••••"
                  className="bg-neutral-800 border-white/10 pr-10"
                  required
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-white transition-colors"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#00F0FF] to-[#00FF94] hover:opacity-90 text-black font-bold rounded-full py-3"
              data-testid="set-password-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Setting up your account...
                </>
              ) : (
                'Create Account & Continue'
              )}
            </Button>
          </form>

          {/* VIP Benefits */}
          <div className="mt-8 p-4 bg-[#00F0FF]/5 border border-[#00F0FF]/20 rounded-xl">
            <h3 className="text-sm font-semibold text-[#00F0FF] mb-2">VIP Creator Benefits:</h3>
            <ul className="text-xs text-neutral-400 space-y-1">
              <li>✓ Early access to the marketplace</li>
              <li>✓ Priority support</li>
              <li>✓ Featured creator badge</li>
              <li>✓ Lower platform fees</li>
            </ul>
          </div>

          {/* Back Link */}
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="text-sm text-neutral-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4 inline mr-1" />
              Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SetPassword;
